import type {
  NewWagerSearchFilters,
  NewWagerSearchParam
} from '@library/types/newWagers';
import { useCallback, useState } from 'react';
import { DEFAULT_ENTRIES_SEARCH_FILTERS } from '@bo-constants/newWagers';
import type { DateRange } from '@library/types/dates';
import { isTimeAfter } from '@library/utils/dateTime';

export const useMemberEntriesFilters = () => {
  const [entriesSearchFilters, setEntriesSearchFilters] =
    useState<NewWagerSearchFilters>(DEFAULT_ENTRIES_SEARCH_FILTERS);

  const handleEntriesDateRange = useCallback(
    (value: DateRange) => {
      const { fromDate: newFromDate, toDate: newToDate } = value;

      const newSelectedEndDate =
        newToDate &&
        isTimeAfter({ dateValue: newFromDate, dateToCompareAgainst: newToDate })
          ? newFromDate
          : newToDate;

      setEntriesSearchFilters({
        ...entriesSearchFilters,
        dateRange: {
          startDate: newFromDate,
          endDate: newSelectedEndDate
        }
      });
    },
    [entriesSearchFilters]
  );

  const handleChangeEntriesFilter = useCallback(
    ({ key, value }: NewWagerSearchParam) => {
      if (key === 'dateRange') {
        handleEntriesDateRange(value as DateRange);
        return;
      }

      setEntriesSearchFilters({
        ...entriesSearchFilters,
        [key]: value
      });
    },
    [entriesSearchFilters, handleEntriesDateRange]
  );

  const handleResetEntriesFilters = () => {
    setEntriesSearchFilters(DEFAULT_ENTRIES_SEARCH_FILTERS);
  };

  const handleSearchParams = ({ id }: { id: NewWagerSearchFilters['id'] }) => {
    setEntriesSearchFilters({ ...DEFAULT_ENTRIES_SEARCH_FILTERS, id });
  };

  return {
    entriesSearchFilters,
    onChangeEntriesFilter: handleChangeEntriesFilter,
    onResetEntriesFilters: handleResetEntriesFilters,
    onSearchParams: handleSearchParams
  };
};
